import * as React from 'react';
import { OwnProps as ListMenuProps } from '../../../common/extensibleEntity/ListMenu';
import { IContextualMenuItem, DefaultButton, DirectionalHint, css } from 'office-ui-fabric-react';
import { ISelectableCellData } from '.';
import { ViewType } from '../ResourceUsageGrid';
import { Validator } from '../../../../validation';
import { formatValue, HUNDRED_PCT } from '../../../utils/common';
import { FormatType } from '../../../../entities/Metadata';
import { EntityType, entityTypeLabelMap, Quantization } from '../../../../entities/common';
import { ProgressIcon } from '../../../common/ProgressFormatter';
import NumberInput from '../../../common/inputs/NumberInput';
import ContextualMenuToggle from '../../../common/ContextualMenuToggle';
import TextInput from '../../../common/inputs/TextInput';
import { CostRateTable } from '../../../../store/CostRateTableStore';
import { ResourceEntityState, ResourceEntityStateConfigMap } from '../../../../store/ResourcesListStore';
import { TimeType } from './common';

export type SetTimeAction = (plan: number | true, viewType?: ViewType, cells?: ISelectableCellData[]) => void;

type SetRateAction = (rate: number) => void;
type SetBillingCodeAction = (billingCode: string) => void;
type SetCostRateTableAction = (costRateTableId: string) => void;
type SetStateInEntityAction = (stateInEntity: ResourceEntityState) => void;

interface Props {
    quantization: Quantization;
    menuProps: ListMenuProps;
    availableTimeTypes: TimeType[];
    timeType: TimeType[];
    onTimeTypeChange: (type: TimeType, checked: boolean) => void;
}

const decimalValidator = Validator.new().decimal().min(0).build();
const integerValidator = Validator.new().int32().min(0).build();

const fullHoursPerPeriod = {
    [Quantization.days]: { maxOption: 8, label: 'hours / day' },
    [Quantization.weeks]: { maxOption: 40, label: 'hours / week' },
    [Quantization.months]: { maxOption: 160, label: 'hours / month' },
    [Quantization.quarters]: { maxOption: 480, label: 'hours / quarter' },
    [Quantization.years]: { maxOption: 2080, label: 'hours / year' }
}

export function buildAutoAllocateCommand(cells: ISelectableCellData[] | undefined, setPlan: SetTimeAction, text: string): IContextualMenuItem {
    return {
        key: 'auto',
        name: 'Auto',
        title: `${text}: Auto`,
        iconProps: { iconName: 'AutoEnhanceOn' },
        onClick: () => setPlan(true, undefined, cells)
    };
}

export function buildSetTimeCommand(timeLabel: string, setTime: SetTimeAction, quantization: Quantization, disabled?: boolean): IContextualMenuItem {
    const text = `Set ${timeLabel}`;
    return {
        key: timeLabel,
        text,
        iconProps: { iconName: 'UserGauge' },
        disabled,
        subMenuProps: {
            items: disabled
                ? [] 
                : [
                    buildAutoAllocateCommand(undefined, setTime, text),
                    buildAllocateCommands(ViewType.Hours, undefined, quantization, setTime, text)!,
                    buildAllocateCommands(ViewType.Percent, undefined, quantization, setTime, text)!,
                    buildAllocateCommands(ViewType.FTE, undefined, quantization, setTime, text)!,
                ]
        }
    };
}

type AllocationParams = {
    validator: Validator;
    iconName: string;
    getOptionValue: (option: number) => number;
    formatValue: (val: number) => string;
    parseValue: (str: string) => number;
}

export function buildAllocationParams(viewType: ViewType, quantization: Quantization): AllocationParams | undefined {
    switch (viewType) {
        case ViewType.Hours:
            return {
                iconName: 'HourGlass',
                validator: decimalValidator,
                getOptionValue: _ => _ * fullHoursPerPeriod[quantization].maxOption,
                formatValue: _ => `${_} ${fullHoursPerPeriod[quantization].label}`,
                parseValue: _ => Number(_.replace(fullHoursPerPeriod[quantization].label, '').trimEnd())
            }
        case ViewType.Percent:
            return {
                iconName: 'CalculatorPercentage',
                validator: integerValidator,
                getOptionValue: _ => _ * HUNDRED_PCT,
                formatValue: _ => formatValue(_, FormatType.Percent),
                parseValue: _ => Number(_.replace('%', '').trimEnd())
            }
        case ViewType.FTE:
            return {
                iconName: 'UserGauge',
                validator: decimalValidator,
                getOptionValue: _ => _,
                formatValue: _ => `${_} FTE`,
                parseValue: _ => Number(_.replace('FTE', '').trimEnd())
            }
        default:
            return undefined;
    }
}

export function buildAllocateCommands(viewType: ViewType, cells: ISelectableCellData[] | undefined, quantization: Quantization, setPlan: SetTimeAction, text: string)
    : IContextualMenuItem | undefined {
    const params = buildAllocationParams(viewType, quantization);
    if (!params) {
        return undefined;
    }
    return {
        key: viewType.toString(),
        name: ViewType[viewType],
        title: `${text}: ${ViewType[viewType]}`,
        iconProps: { iconName: params.iconName },
        subMenuProps: {
            items: [...[0, 0.25, 0.5, 0.75, 1].map(_ => ({
                key: _.toString(),
                name: params.formatValue(params.getOptionValue(_)),
                className: "progress-formatter",
                onRenderIcon: () => <ProgressIcon progress={_ * 100} />,
                iconProps: {},
                onClick: () => setPlan(params.getOptionValue(_), viewType, cells)
            })), {
                key: "custom",
                onRender: (item, dismissMenu) => (
                    <div className="resource-usage-input">
                        <NumberInput
                            inputProps={{ placeholder: 'type custom value' }}
                            validator={params.validator}
                            onEditComplete={(value) => { if (value !== null) { setPlan(value, viewType, cells); dismissMenu(); } }}
                        />
                    </div>
                ),
            }]
        }
    };
}

export function buildSetRateCommand(setRate: SetRateAction): IContextualMenuItem | undefined {
    return {
        key: "setRate",
        name: "Set Charge Rate",
        iconProps: { iconName: "money" },
        subMenuProps: {
            items: [{
                key: "custom",
                onRender: (item, dismissMenu) => (
                    <div className="resource-usage-input">
                        <NumberInput
                            inputProps={{ placeholder: "type charge rate" }}
                            validator={decimalValidator}
                            onEditComplete={(value) => {
                                if (value !== null) {
                                    setRate(value);
                                    dismissMenu();
                                }
                            }}
                        />
                    </div>
                ),
            }],
        },
    };
}

export function buildSetBillingCodeCommand(setBillingCode: SetBillingCodeAction): IContextualMenuItem | undefined {
    return {
            key: "setBillingCode",
            name: "Set Billing Code",
            iconProps: { iconName: "money" },
            subMenuProps: {
                items: [{
                    key: "custom",
                    onRender: (item, dismissMenu) => (
                        <div className="resource-usage-input">
                            <TextInput
                                inputProps={{ placeholder: "type billing code" }}
                                onEditComplete={(value) => {
                                    if (value !== null && typeof value === "string") {
                                        setBillingCode(value);
                                        dismissMenu();
                                    }
                                }}
                            />
                        </div>
                    ),
                }],
            },
        };
}

export function buildSetStateInEntityCommand(setStateInEntity: SetStateInEntityAction, entityType: EntityType,
    currentState?: ResourceEntityState): IContextualMenuItem {
    return {
        key: "setStateInEntity",
        name: `Set State in ${entityTypeLabelMap[entityType].singular}`,
        iconProps: { iconName: "PlayerSettings" },
        subMenuProps: {
            items: [ResourceEntityState.Active, ResourceEntityState.Inactive].map(state => ({
                key: ResourceEntityState[state],
                name: ResourceEntityStateConfigMap[state].title,
                canCheck: true,
                checked: currentState === state,
                onClick: () => setStateInEntity(state)
            }))
        },
    };
}

export function buildSetCostRateTableCommand(setCostRateTable: SetCostRateTableAction, costRateTables: CostRateTable[]): IContextualMenuItem | undefined {
    if (!costRateTables?.length) {
        return undefined;
    }
    return {
        key: "setCostRateTable",
        name: "Set Cost Rate Table",
        iconProps: { iconName: "Table" },
        subMenuProps: {
            items: costRateTables.map(table => ({
                key: table.id,
                text: table.name,
                onClick: () => setCostRateTable(table.id)
            }))
        },
    };
}

export const TimeTypeSelector = <T extends string>(props: {
    timeType: T[],
    allTimeTypes: { type: T, title: string, className: string }[],
    onTimeTypeChange: (timeType: T, ckecked: boolean) => void
}) => {
    const { timeType, allTimeTypes, onTimeTypeChange } = props;
    const renderLabel = (_: { type: T, title: string, className: string }) =>
        <React.Fragment key={_.type}><span className={css("time-type-icon", _.className)} />{_.title}</React.Fragment>;
    return <DefaultButton
        className="dropdown-button time-type-selector"
        iconProps={{ iconName: 'Clock' }}
        onRenderText={() => <span key='_'>
            Time Type: {allTimeTypes.filter(_ => timeType.includes(_.type)).map((_, i, selected) =>
                <React.Fragment key={_.type}>{renderLabel(_)}{i < selected.length - 1 && ","}</React.Fragment>)}
        </span>}
        menuProps={{
            className: "time-type-menu",
            directionalHint: DirectionalHint.bottomRightEdge,
            items: allTimeTypes.map(_ => ({
                key: _.type,
                onRender: () => <ContextualMenuToggle
                    label={renderLabel(_)}
                    disabled={timeType.length === 1 && timeType.some(x => x === _.type)}
                    checked={timeType.some(x => x === _.type)}
                    onChange={(e, checked?: boolean) => onTimeTypeChange(_.type, !!checked)}
                />
            }))
        }} />
}